import { UserServiceAccount } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { TitleText } from '@/components/TitleText';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { getApiDocumentationUrl } from '@/services/api';
import { ContentCopy, Delete, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatDistanceToNow } from 'date-fns';

export const StyledServiceAccountAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  margin: 0,
  '&:before': {
    display: 'none',
  },
  '& .MuiAccordionSummary-root': {
    padding: '24px',
    minHeight: 'auto',
    '&.Mui-expanded': {
      minHeight: 'auto',
    },
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      borderRadius: '50%',
    },
  },
  '& .MuiAccordionDetails-root': {
    padding: '0 24px 24px 32px',
  },
}));

const InfoRowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  borderBottom: '1px solid',
  borderColor: theme.palette.divider,
}));

const InfoRowLabel = styled(Box)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
  minWidth: '100px',
}));

const InfoRowValueContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  justifyContent: 'flex-end',
}));

const InfoRowValue = styled(Box, {
  shouldForwardProp: property => property !== 'monospace',
})<{ monospace?: boolean }>(({ monospace, theme }) => ({
  color: theme.palette.text.primary,
  fontFamily: monospace ? 'monospace' : 'inherit',
  fontSize: monospace ? '13px' : 'inherit',
  wordBreak: 'break-all',
}));

const DeleteButtonContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  textAlign: 'right',
}));

const StyledDeleteButton = styled(Button)({
  textTransform: 'none',
  fontSize: '13px',
  '&:hover': {
    backgroundColor: 'rgba(211, 47, 47, 0.04)',
  },
});

const InfoRow = ({
  label,
  value,
  onCopy,
  monospace = false,
}: {
  label: string;
  value: string;
  onCopy?: () => void;
  monospace?: boolean;
}) => {
  return (
    <InfoRowContainer>
      <InfoRowLabel>
        <BodyText style={{ fontWeight: 500 }}>{label}</BodyText>
      </InfoRowLabel>
      <InfoRowValueContainer>
        <InfoRowValue monospace={monospace}>
          <BodyText style={{}}>{value}</BodyText>
        </InfoRowValue>
        {onCopy && (
          <Tooltip title={`Copy ${label}`}>
            <IconButton size='small' onClick={onCopy} sx={{ ml: 0.5 }} aria-label={`Copy ${label}`}>
              <ContentCopy fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
      </InfoRowValueContainer>
    </InfoRowContainer>
  );
};

export const ServiceAccountCard = ({
  serviceAccount,
  onDelete,
}: {
  serviceAccount: UserServiceAccount;
  onDelete: () => void;
}) => {
  const { copy } = useCopyToClipboard();

  return (
    <StyledServiceAccountAccordion defaultExpanded={true}>
      <AccordionSummary expandIcon={<ExpandMore />} aria-label='Expand service account details'>
        <TitleText>My Service Account</TitleText>
      </AccordionSummary>
      <AccordionDetails>
        <Alert severity='info' sx={{ mb: 3 }}>
          Use these credentials for programmatic API access.{' '}
          <Link href={getApiDocumentationUrl()} target='_blank' rel='noopener noreferrer'>
            Documentation →
          </Link>
        </Alert>
        <Box>
          <InfoRow label='Name' value={serviceAccount.name} />
          <InfoRow
            label='Client ID'
            value={serviceAccount.clientId}
            onCopy={() => void copy(serviceAccount.clientId, 'Client ID')}
            monospace
          />
          <InfoRow
            label='Created'
            value={formatDistanceToNow(new Date(serviceAccount.createdAt), {
              addSuffix: true,
            })}
          />
        </Box>

        <DeleteButtonContainer>
          <StyledDeleteButton
            variant='text'
            color='error'
            size='small'
            onClick={onDelete}
            startIcon={<Delete fontSize='small' />}
          >
            Delete
          </StyledDeleteButton>
        </DeleteButtonContainer>
      </AccordionDetails>
    </StyledServiceAccountAccordion>
  );
};
